<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
  useId,
} from "vue";
import { querySchema, type Field, type QuerySpec } from "@yawhub/core";
import { api, errorText, dateTime, useSession } from "../api";
import {
  AddIcon,
  SaveIcon,
  RefreshIcon,
  ChartIcon,
  AiIcon,
  CloseIcon,
  CogIcon,
  DragHandleIcon,
} from "../icons";
import ResultView from "./ResultView.vue";
import PublicShare from "./PublicShare.vue";
import TableAppearance from "./TableAppearance.vue";
import ColumnSelection from "./ColumnSelection.vue";
import QueryBuilder from "./QueryBuilder.vue";
import PeriodField from "./PeriodField.vue";
import { filterDescription } from "../queryPresentation";
import { useCardReorder } from "../useCardReorder";
import { vPosition } from "../directives/geometry";
const props = defineProps<{
  dataset: string;
  fields: Field[];
  views: any[];
  canWrite: boolean;
  mode: "forms" | "dashboard";
  dashboard?: any;
  dashboards?: any[];
}>();
const emit = defineEmits(["changed"]);
const base = "/api/datasets/" + props.dataset,
  session = useSession();
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));
const selected = ref<any>(null),
  editing = ref(false),
  name = ref(""),
  kind = ref("table"),
  query = ref<QuerySpec>(querySchema.parse({})),
  columns = ref<string[]>([]),
  scaleBars = ref<string[]>([]),
  showUpdatedAt = ref(false),
  period = ref<any>(),
  result = ref<any>(),
  busy = ref(false),
  error = ref(""),
  notice = ref(""),
  question = ref(""),
  answer = ref<any>();
const targetDashboard = ref(""),
  rankCount = ref<3 | 5>(3),
  calendarYear = ref(new Date().getFullYear()),
  calendarTimezone = ref("Europe/Copenhagen");
const manual = ref(false),
  questionInput = ref<HTMLTextAreaElement>(),
  proposalTitle = ref<HTMLHeadingElement>(),
  viewNameInput = ref<HTMLInputElement>(),
  createButton = ref<HTMLButtonElement>(),
  aiBusy = ref(false);
const aiFirst = computed(
  () => props.mode === "dashboard" && !selected.value && !manual.value,
);
const dashboardResults = ref<Record<string, any>>({}),
  dashboardErrors = ref<Record<string, string>>({}),
  dashboardLoading = ref(false);
const eligible = computed(() =>
  props.fields.filter((f) => f.active && f.queryable && f.type !== "json"),
);
const numerics = computed(() =>
  eligible.value.filter((f) =>
    ["integer", "decimal", "rating"].includes(f.type),
  ),
);
const examples = computed(() => {
  const metric = numerics.value[0],
    date = eligible.value.find((f) => ["date", "datetime"].includes(f.type)),
    category = eligible.value.find((f) => ["text", "enum"].includes(f.type));
  return [
    ...(date && metric
      ? [`Vis gennemsnitlig ${metric.label.toLowerCase()} pr. uge`]
      : []),
    ...(category
      ? [`Fordel antal rækker efter ${category.label.toLowerCase()}`]
      : []),
    "Hvor mange rækker er der i alt?",
  ].slice(0, 3);
});
const savedDashboardViews = computed(() =>
  props.views
    .filter(
      (v) =>
        (props.dashboard
          ? v.dashboard_id === props.dashboard.id
          : v.config?.onDashboard) && v.kind !== "form",
    )
    .sort(
      (a, b) =>
        a.dashboard_position - b.dashboard_position ||
        a.name.localeCompare(b.name),
    ),
);
const pendingOrder = ref<string[] | null>(null),
  dashboardGrid = ref<HTMLElement>(),
  orderHelpId = useId();
let savedOrderRevision: string | undefined;
const dashboardViews = computed(() => {
  const cards = savedDashboardViews.value;
  if (!pendingOrder.value) return cards;
  const byId = new Map(cards.map((card) => [card.id, card]));
  return pendingOrder.value.map((id) => byId.get(id)).filter(Boolean);
});
watch(
  () => savedDashboardViews.value.map((v) => v.id).join(","),
  () => {
    pendingOrder.value = null;
  },
);
watch(
  () => props.dashboard?.revision,
  () => {
    savedOrderRevision = undefined;
  },
);
const {
  liftedId,
  targetId,
  sourceIndex,
  targetIndex,
  dragging,
  saving: orderSaving,
  status: orderStatus,
  position: dragPosition,
  name: draggedName,
  pointerDown: startCardDrag,
  click: selectCardPosition,
  keydown: moveCardWithKeyboard,
} = useCardReorder({
  cards: () => dashboardViews.value,
  enabled: () =>
    props.canWrite && !!props.dashboard && !busy.value && !editing.value,
  grid: dashboardGrid,
  save: async (ids) => {
    error.value = "";
    pendingOrder.value = ids;
    try {
      const updated = await api(base + "/dashboards/" + props.dashboard.id, {
        method: "PUT",
        revision: savedOrderRevision ?? props.dashboard.revision,
        body: { name: props.dashboard.name, viewIds: ids },
      });
      savedOrderRevision = updated.revision;
      emit("changed");
    } catch (e) {
      pendingOrder.value = null;
      throw e;
    }
  },
  onError: (e) => {
    error.value = errorText(e);
  },
});
const canShare = computed(() =>
  ["owner", "admin"].includes(
    session.organizations.find((o: any) => o.id === session.organization)?.role,
  ),
);
const ranking = computed({
  get: () =>
    query.value.sort?.metricAlias
      ? `${query.value.sort.direction}:${query.value.limit}`
      : "",
  set: (value: string) => {
    if (!value) {
      delete query.value.sort;
      query.value.limit = 100;
      return;
    }
    const [direction, count] = value.split(":");
    query.value.sort = {
      metricAlias: query.value.metrics[0]?.alias,
      direction: direction as "asc" | "desc",
    };
    query.value.limit = Number(count);
  },
});
watch(
  () => query.value.metrics[0]?.alias,
  (alias) => {
    if (query.value.sort?.metricAlias && alias)
      query.value.sort.metricAlias = alias;
  },
);
const kindLabel = (value: string) =>
  ({
    table: "Tabel",
    line: "Linjegraf",
    bar: "Søjlegraf",
    kpi: "Nøgletal",
    form: "Formular",
    ranking: "Top / bund",
    calendar: "Aktivitetskalender",
  })[value] ?? value;
const ready = computed(
  () =>
    name.value.trim() &&
    (!["line", "bar", "ranking", "calendar"].includes(kind.value) ||
      query.value.dimensions.length === 1) &&
    (!query.value.metrics.length ||
      query.value.metrics[0]?.op === "count_records" ||
      !!query.value.metrics[0]?.fieldId) &&
    (kind.value !== "table" || columns.value.length > 0),
);
function payload() {
  return {
    name: name.value.trim() || "Forhåndsvisning",
    kind: kind.value,
    dashboardId: targetDashboard.value || props.dashboard?.id,
    query: query.value,
    config: {
      ...selected.value?.config,
      refreshSeconds: 60,
      fieldIds: columns.value,
      scaleBars: scaleBars.value.filter((id) =>
        props.fields.some(
          (f) => f.id === id && f.active && f.type === "rating",
        ),
      ),
      showUpdatedAt: showUpdatedAt.value,
      onDashboard: true,
      rankingCount: rankCount.value,
      calendarYear: calendarYear.value,
      calendarTimezone: calendarTimezone.value,
      window: period.value,
    },
  };
}
async function act(fn: () => Promise<void>) {
  busy.value = true;
  error.value = "";
  notice.value = "";
  try {
    await fn();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
function setKind(value: string) {
  kind.value = value;
  if (
    !["bar", "line", "ranking"].includes(value) &&
    query.value.sort?.metricAlias
  )
    ranking.value = "";
  if (["table", "form"].includes(value)) {
    query.value.metrics = [];
    query.value.dimensions = [];
  } else {
    if (!query.value.metrics.length)
      query.value.metrics = [
        numerics.value[0]
          ? { op: "sum", fieldId: numerics.value[0].id, alias: "resultat" }
          : { op: "count_records", alias: "antal" },
      ];
    if (value === "kpi") query.value.dimensions = [];
    else if (
      !query.value.dimensions.length ||
      (["line", "calendar"].includes(value) &&
        !eligible.value.some(
          (f) =>
            f.id === query.value.dimensions[0]?.fieldId &&
            ["date", "datetime"].includes(f.type),
        ))
    ) {
      const field = ["line", "calendar"].includes(value)
        ? eligible.value.find((f) => ["date", "datetime"].includes(f.type))
        : eligible.value.find((f) => ["text", "enum"].includes(f.type));
      query.value.dimensions = [];
      if (field)
        query.value.dimensions = [
          {
            fieldId: field.id,
            ...(["line", "calendar"].includes(value)
              ? { bucket: "day" as const }
              : {}),
          },
        ];
    }
  }
  if (value === "ranking") ranking.value = "desc:" + rankCount.value;
  if (value === "calendar" && query.value.dimensions[0])
    query.value.dimensions[0].bucket = "day";
  result.value = undefined;
}
function create(value = "table") {
  targetDashboard.value = props.dashboard?.id ?? "";
  rankCount.value = 3;
  calendarYear.value = new Date().getFullYear();
  calendarTimezone.value = "Europe/Copenhagen";
  selected.value = null;
  name.value = "";
  kind.value = "table";
  query.value = querySchema.parse({});
  columns.value = props.fields
    .filter((f) => f.active && !f.hidden)
    .map((f) => f.id);
  scaleBars.value = [];
  showUpdatedAt.value = false;
  period.value = undefined;
  answer.value = null;
  question.value = "";
  manual.value = props.mode !== "dashboard" || !session.capabilities.ai;
  result.value = undefined;
  editing.value = true;
  setKind(value);
  notice.value = "";
  error.value = "";
  void nextTick(() => questionInput.value?.focus());
}
async function open(view: any) {
  selected.value = clone(view);
  targetDashboard.value = view.dashboard_id;
  rankCount.value = view.config?.rankingCount ?? 3;
  calendarYear.value = view.config?.calendarYear ?? new Date().getFullYear();
  calendarTimezone.value = view.config?.calendarTimezone ?? "Europe/Copenhagen";
  name.value = view.name;
  kind.value = view.kind;
  query.value = querySchema.parse(clone(view.query));
  columns.value = [
    ...(view.config?.fieldIds ??
      props.fields.filter((f) => f.active && !f.hidden).map((f) => f.id)),
  ];
  scaleBars.value = [...(view.config?.scaleBars ?? [])];
  showUpdatedAt.value = !!view.config?.showUpdatedAt;
  period.value = clone(view.config?.window ?? null) ?? undefined;
  editing.value = true;
  answer.value = null;
  await run();
}
async function run() {
  await act(async () => {
    result.value = await api(base + "/views/preview", {
      method: "POST",
      body: payload(),
    });
  });
}
async function save() {
  await act(async () => {
    const view = await api(
      base + "/views" + (selected.value ? "/" + selected.value.id : ""),
      {
        method: selected.value ? "PUT" : "POST",
        revision: selected.value?.revision,
        body: payload(),
      },
    );
    selected.value = view;
    emit("changed");
    if (props.mode === "dashboard") {
      editing.value = false;
      await nextTick(() => createButton.value?.focus());
    }
    notice.value = "Kortet er gemt.";
    result.value = await api(base + "/views/" + view.id + "/run", {
      method: "POST",
    });
  });
}
async function filter(q: QuerySpec) {
  query.value = q;
  await run();
}
async function loadDashboard() {
  dashboardLoading.value = true;
  await Promise.all(
    dashboardViews.value.map(async (v) => {
      try {
        const next = await api(base + "/views/" + v.id + "/run", {
          method: "POST",
        });
        const previous = dashboardResults.value[v.id];
        // Keep pagination and an inspected value stable when a poll finds no changes.
        if (
          !previous ||
          previous.metadata.dataRevision !== next.metadata.dataRevision ||
          previous.metadata.schemaVersion !== next.metadata.schemaVersion ||
          JSON.stringify(previous.query) !== JSON.stringify(next.query)
        )
          dashboardResults.value[v.id] = next;
        delete dashboardErrors.value[v.id];
      } catch (e) {
        dashboardErrors.value[v.id] = errorText(e);
      }
    }),
  );
  dashboardLoading.value = false;
}
async function saveInsight() {
  await act(async () => {
    await api(base + "/insights", {
      method: "POST",
      body: { question: question.value, query: answer.value.result.query },
    });
    notice.value = "Indsigten er gemt med dataversion.";
  });
}
async function ask() {
  if (busy.value || !question.value.trim()) return;
  aiBusy.value = true;
  answer.value = null;
  result.value = undefined;
  await act(async () => {
    const response = await api(base + "/ai/question", {
      method: "POST",
      body: { question: question.value.trim() },
    });
    answer.value = response;
    query.value = answer.value.result.query;
    result.value = answer.value.result;
    period.value = undefined;
    const dimensions = query.value.dimensions;
    const metric = query.value.metrics[0];
    const numericResult =
      metric &&
      (["sum", "avg", "count_records", "count_values"].includes(metric.op) ||
        numerics.value.some((f) => f.id === metric.fieldId));
    if (
      query.value.metrics.length !== 1 ||
      dimensions.length > 1 ||
      !numericResult
    )
      kind.value = "table";
    else if (!dimensions.length) kind.value = "kpi";
    else
      kind.value = props.fields.some(
        (f) =>
          f.id === dimensions[0]?.fieldId &&
          ["date", "datetime"].includes(f.type),
      )
        ? "line"
        : "bar";
    if (aiFirst.value || !name.value.trim())
      name.value = question.value.trim().slice(0, 120);
  });
  aiBusy.value = false;
  if (answer.value && aiFirst.value)
    await nextTick(() => proposalTitle.value?.focus());
}
function showManual() {
  manual.value = true;
  void nextTick(() => viewNameInput.value?.focus());
}
async function saveDashboard() {
  await save();
  if (!error.value) {
    editing.value = false;
    await nextTick(() => createButton.value?.focus());
  }
}
watch(
  () => props.mode,
  () => {
    editing.value = false;
    error.value = "";
    notice.value = "";
    if (props.mode === "dashboard") void loadDashboard();
  },
);
watch(
  () => dashboardViews.value.map((v) => v.id + ":" + v.revision).join(","),
  () => {
    if (props.mode === "dashboard") void loadDashboard();
  },
);
let timer: ReturnType<typeof setInterval>;
onMounted(() => {
  if (props.mode === "dashboard") void loadDashboard();
  timer = setInterval(() => {
    if (props.mode === "dashboard" && !editing.value) void loadDashboard();
  }, 60000);
});
onUnmounted(() => clearInterval(timer));
</script>
<template>
  <div class="section-heading">
    <div>
      <h2>
        {{
          mode === "dashboard"
            ? (dashboard?.name ?? "Dit overblik")
            : "Formularer"
        }}
      </h2>
      <p class="muted">
        {{
          mode === "dashboard"
            ? "Følg dine data i grafer, tabeller og nøgletal."
            : "Gem udvalgte felter som en formular til indtastning og deling."
        }}
      </p>
    </div>
    <div class="heading-actions">
      <button
        v-if="mode === 'dashboard'"
        class="icon-button"
        :disabled="dashboardLoading"
        aria-label="Opdatér dashboard"
        @click="loadDashboard"
      >
        <RefreshIcon :size="18" /></button
      ><button
        v-if="canWrite"
        ref="createButton"
        class="button primary"
        :disabled="busy"
        @click="create(mode === 'dashboard' ? 'line' : 'form')"
      >
        <AddIcon :size="17" />{{
          mode === "dashboard" ? "Ny graf eller tal" : "Ny formular"
        }}
      </button>
    </div>
  </div>
  <p v-if="error" class="error" role="alert">{{ error }}</p>
  <p v-if="notice" class="notice" role="status">{{ notice }}</p>
  <div v-if="mode === 'forms' && views.length" class="saved-view-list">
    <button
      v-for="view in views"
      :key="view.id"
      :class="{ selected: selected?.id === view.id && editing }"
      @click="open(view)"
    >
      <strong>{{ view.name }}</strong
      ><span>{{ kindLabel(view.kind) }}</span>
    </button>
  </div>
  <section v-if="editing" class="view-editor">
    <div class="section-heading">
      <h3>
        {{
          selected
            ? canWrite
              ? "Redigér kort"
              : "Kort"
            : aiFirst
              ? "Hvad vil du følge?"
              : mode === "dashboard"
                ? "Ny graf eller tal"
                : "Ny formular"
        }}
      </h3>
      <button
        class="icon-button"
        aria-label="Luk kort"
        :disabled="busy"
        @click="editing = false"
      >
        <CloseIcon :size="18" />
      </button>
    </div>
    <div v-if="aiFirst" class="dashboard-prompt">
      <p class="muted">
        Beskriv en graf, en tabel eller et tal. Du ser forslaget, før du gemmer
        det på dashboardet.
      </p>
      <form
        class="dashboard-question"
        :aria-busy="aiBusy"
        @submit.prevent="ask"
      >
        <label for="dashboard-question">Beskriv dit overblik</label>
        <p class="small muted">
          AI modtager dit spørgsmål, feltnavne, typer og tidszone. Rækker sendes
          ikke. Undlad følsomme oplysninger.
          <RouterLink to="/connections">AI-indstillinger</RouterLink>
        </p>
        <textarea
          id="dashboard-question"
          ref="questionInput"
          v-model="question"
          rows="3"
          maxlength="2000"
          required
          :disabled="busy"
          placeholder="Fx: Vis gennemsnittet pr. uge, så jeg kan følge udviklingen."
        />
        <div class="prompt-actions">
          <button class="button primary" :disabled="busy || !question.trim()">
            <AiIcon :size="18" />{{
              aiBusy
                ? "Laver forslag…"
                : answer
                  ? "Lav et nyt forslag"
                  : "Lav forslag"
            }}
          </button>
          <button
            type="button"
            class="text-button"
            :disabled="busy"
            @click="showManual"
          >
            Byg selv med felter
          </button>
        </div>
        <p v-if="aiBusy" class="muted small" role="status">
          Finder de relevante felter og beregner dine data…
        </p>
      </form>
      <div
        v-if="!answer"
        class="prompt-examples"
        aria-label="Eksempler på spørgsmål"
      >
        <span class="muted small">Prøv fx</span>
        <button
          v-for="example in examples"
          :key="example"
          class="text-button"
          :disabled="busy"
          @click="
            question = example;
            questionInput?.focus();
          "
        >
          {{ example }}
        </button>
      </div>
    </div>
    <p
      v-if="mode === 'dashboard' && !selected && !session.capabilities.ai"
      class="notice"
    >
      AI er ikke tilsluttet. Du kan stadig lave din graf med felterne nedenfor.
    </p>
    <form
      v-if="session.capabilities.ai && !aiFirst"
      class="ai-question"
      @submit.prevent="ask"
    >
      <AiIcon :size="19" /><input
        v-model="question"
        aria-label="Spørgsmål til dine data"
        placeholder="Fx: Hvor mange km løb jeg sidste måned?"
        required
        maxlength="2000"
        :disabled="busy"
      /><button class="button secondary" :disabled="busy">
        {{ busy ? "Undersøger…" : "Spørg" }}
      </button>
    </form>
    <p v-if="answer" class="notice" role="status">
      {{ answer.interpretation }}
    </p>
    <section
      v-if="aiFirst && answer"
      class="dashboard-proposal"
      aria-label="Forslag til dashboard"
    >
      <div class="section-heading">
        <h3 ref="proposalTitle" tabindex="-1">Dit forslag</h3>
        <span class="muted small">{{ kindLabel(kind) }} · Ikke gemt endnu</span>
      </div>
      <ResultView :result="result" :kind="kind" :field-ids="columns" compact />
      <form class="proposal-save" @submit.prevent="saveDashboard">
        <label
          >Kortets navn<input
            v-model="name"
            required
            maxlength="120"
            :disabled="busy"
        /></label>
        <div class="form-actions">
          <button
            type="submit"
            class="button primary"
            :disabled="busy || !ready"
          >
            <SaveIcon :size="16" />Gem på dashboard
          </button>
          <button
            type="button"
            class="button secondary"
            :disabled="busy"
            @click="showManual"
          >
            Tilpas felter og udseende
          </button>
        </div>
      </form>
    </section>
    <button
      v-if="answer && canWrite && !aiFirst"
      class="text-button"
      :disabled="busy"
      @click="saveInsight"
    >
      Gem indsigt med dataversion
    </button>
    <form v-if="!aiFirst" class="form-stack" @submit.prevent="save">
      <div class="form-grid">
        <label
          >Kortets navn<input
            ref="viewNameInput"
            v-model="name"
            required
            maxlength="120"
            placeholder="Fx Kilometer pr. uge" /></label
        ><label v-if="mode === 'dashboard'"
          >Vis som<select
            :value="kind"
            @change="setKind(($event.target as HTMLSelectElement).value)"
          >
            <option value="table">Tabel</option>
            <option value="line">Linjegraf</option>
            <option value="bar">Søjlegraf</option>
            <option value="kpi">Nøgletal</option>
            <option value="ranking">Top / bund</option>
            <option value="calendar">Aktivitetskalender</option>
          </select></label
        >
      </div>
      <div v-if="query.metrics[0]" class="form-grid">
        <label
          >Hvad vil du måle?<select v-model="query.metrics[0].op">
            <option value="sum">Samlet værdi</option>
            <option value="avg">Gennemsnit</option>
            <option value="count_records">Antal rækker</option>
            <option value="count_values">Antal udfyldte værdier</option>
            <option value="min">Laveste værdi</option>
            <option value="max">Højeste værdi</option>
          </select></label
        ><label v-if="query.metrics[0].op !== 'count_records'"
          >Mål i<select v-model="query.metrics[0].fieldId" required>
            <option :value="undefined" disabled>Vælg felt</option>
            <option
              v-for="f in ['sum', 'avg'].includes(query.metrics[0].op)
                ? numerics
                : eligible"
              :key="f.id"
              :value="f.id"
            >
              {{ f.label }}{{ f.unit ? " · " + f.unit : "" }}
            </option>
          </select></label
        >
      </div>
      <div
        v-if="['line', 'bar', 'ranking', 'calendar'].includes(kind)"
        class="form-grid"
      >
        <label
          >Fordel efter<select
            :value="query.dimensions[0]?.fieldId ?? ''"
            required
            @change="
              query.dimensions = [
                {
                  fieldId: ($event.target as HTMLSelectElement).value,
                  ...(['line', 'calendar'].includes(kind)
                    ? { bucket: 'day' as const }
                    : {}),
                },
              ]
            "
          >
            <option disabled value="">Vælg felt</option>
            <option
              v-for="f in ['line', 'calendar'].includes(kind)
                ? eligible.filter((f) => ['date', 'datetime'].includes(f.type))
                : eligible"
              :key="f.id"
              :value="f.id"
            >
              {{ f.label }}
            </option>
          </select></label
        ><label
          v-if="
            kind !== 'calendar' &&
            query.dimensions[0] &&
            ['date', 'datetime'].includes(
              fields.find((f) => f.id === query.dimensions[0]?.fieldId)?.type ??
                '',
            )
          "
          >Saml pr.<select v-model="query.dimensions[0].bucket">
            <option value="day">Dag</option>
            <option value="week">Uge</option>
            <option value="month">Måned</option>
          </select></label
        >
      </div>
      <div v-if="kind === 'calendar'" class="form-grid">
        <label
          >År<input
            v-model.number="calendarYear"
            type="number"
            min="1900"
            max="2200"
            required /></label
        ><label>Tidszone<input v-model="calendarTimezone" required /></label>
      </div>
      <label v-if="kind === 'ranking'"
        >Antal placeringer<select v-model="rankCount">
          <option :value="3">3</option>
          <option :value="5">5</option>
        </select></label
      >
      <label v-if="dashboards?.length"
        >Dashboard<select v-model="targetDashboard">
          <option v-for="d in dashboards" :key="d.id" :value="d.id">
            {{ d.name }}
          </option>
        </select></label
      >
      <PeriodField
        v-if="!['form', 'calendar'].includes(kind)"
        v-model="period"
        :fields="fields"
      />
      <label
        v-if="
          query.metrics.length &&
          query.dimensions.length &&
          !['ranking', 'calendar'].includes(kind)
        "
        >Rangering<select v-model="ranking">
          <option value="">Alle grupper</option>
          <option value="desc:3">Top 3</option>
          <option value="desc:5">Top 5</option>
          <option value="asc:3">Bund 3</option>
          <option value="asc:5">Bund 5</option></select
        ><span class="muted small"
          >Sorteret efter den første måling. Tomme værdier kommer sidst.</span
        ></label
      >
      <ColumnSelection
        v-if="['table', 'form'].includes(kind)"
        v-model="columns"
        :fields="fields"
      />
      <TableAppearance
        v-if="kind === 'table' && !query.metrics.length"
        v-model:bars="scaleBars"
        v-model:updated="showUpdatedAt"
        :fields="fields"
        :columns="columns"
      />
      <details v-if="kind !== 'form'" class="view-query-options">
        <summary>Flere filtre og beregninger</summary>
        <QueryBuilder
          v-model="query"
          :fields="fields"
          :busy="busy"
          @run="run"
        />
      </details>
      <div class="form-actions">
        <button
          type="button"
          class="button secondary"
          :disabled="busy"
          @click="run"
        >
          {{ busy ? "Beregner…" : "Vis resultat" }}</button
        ><button
          v-if="canWrite"
          class="button primary"
          :disabled="busy || !ready"
        >
          <SaveIcon :size="16" />{{ selected ? "Gem ændringer" : "Gem kort" }}
        </button>
      </div>
    </form>
    <div class="active-filter-list" v-if="query.filters.length && !aiFirst">
      <button
        v-for="(f, i) in query.filters"
        :key="i"
        class="button secondary"
        @click="
          filter({
            ...query,
            filters: query.filters.filter((_, index) => index !== i),
          })
        "
      >
        {{ filterDescription(f, fields) }}
        <CloseIcon :size="13" /><span class="sr-only">Fjern filter</span>
      </button>
    </div>
    <div v-if="kind === 'form' && selected" class="notice">
      <RouterLink :to="'/forms/' + selected.id" class="button secondary"
        >Åbn formular</RouterLink
      >
    </div>
    <ResultView
      v-else-if="result && !aiFirst"
      :result="result"
      :kind="kind"
      :field-ids="columns"
      :scale-bars="scaleBars"
      :show-updated-at="showUpdatedAt"
      :query="query"
      :view-config="payload().config"
      :preview-definition="payload()"
      filterable
      @filter="filter"
    />
    <p v-if="result" class="muted small result-provenance">
      Beregnet {{ dateTime(result.metadata.executedAt) }} ·
      {{ result.metadata.matchedRecords ?? result.rows.length }} rækker{{
        result.nextCursor ? " på denne side" : " matcher"
      }}
    </p>
    <details v-if="answer?.comparison">
      <summary>Sammenligningsperiode</summary>
      <ResultView :result="answer.comparison" kind="table" />
    </details>
    <PublicShare
      v-if="selected && kind !== 'form' && canShare"
      :key="selected.id"
      :dataset="dataset"
      :view="selected.id"
    />
  </section>
  <template v-else-if="mode === 'dashboard'">
    <p :id="orderHelpId" class="sr-only">
      Træk for at flytte kortet. Du kan også vælge håndtaget og derefter
      håndtaget på den ønskede placering. Med tastatur: tryk mellemrum, brug
      piletasterne, og tryk mellemrum igen for at gemme. Escape annullerer.
    </p>
    <p class="sr-only" role="status" aria-live="polite">{{ orderStatus }}</p>
    <div v-if="!dashboardViews.length" class="empty-state">
      <ChartIcon :size="30" />
      <h3>De vigtigste tal, samlet ét sted</h3>
      <p>
        Lav fx en graf over distance eller en fordeling af aktiviteter. Vælg
        selv, hvilke kort der skal være her.
      </p>
      <button
        v-if="canWrite"
        class="button secondary"
        :disabled="busy"
        @click="create('line')"
      >
        Lav din første graf
      </button>
    </div>
    <div
      v-else
      ref="dashboardGrid"
      class="dashboard-grid"
      :class="{ 'is-reordering': liftedId }"
      :aria-busy="orderSaving"
    >
      <article
        v-for="view in dashboardViews"
        :key="view.id"
        :data-card-id="view.id"
        class="dashboard-view"
        :class="{
          compact: view.kind === 'kpi',
          wide: ['calendar', 'table'].includes(view.kind),
          'is-lifted': liftedId === view.id,
          'drop-before': targetId === view.id && targetIndex < sourceIndex,
          'drop-after': targetId === view.id && targetIndex > sourceIndex,
        }"
      >
        <div class="section-heading card-heading">
          <button
            v-if="canWrite && dashboard && dashboardViews.length > 1"
            type="button"
            class="icon-button card-drag-handle"
            :data-drag-handle="view.id"
            :disabled="busy || orderSaving"
            :aria-label="'Flyt ' + view.name"
            :aria-describedby="orderHelpId"
            :aria-pressed="liftedId === view.id"
            title="Træk for at flytte"
            @pointerdown="startCardDrag($event, view.id)"
            @click="selectCardPosition(view.id)"
            @keydown="moveCardWithKeyboard($event, view.id)"
          >
            <DragHandleIcon :size="18" />
          </button>
          <h3>{{ view.name }}</h3>
          <button
            v-if="canWrite"
            type="button"
            class="icon-button card-settings"
            :aria-label="'Åbn og redigér ' + view.name"
            title="Åbn og redigér"
            :disabled="orderSaving || !!liftedId"
            @click="open(view)"
          >
            <CogIcon :size="19" />
          </button>
          <button v-else class="text-button" @click="open(view)">Åbn</button>
        </div>
        <p v-if="dashboardErrors[view.id]" class="error" role="alert">
          {{ dashboardErrors[view.id] }}
        </p>
        <ResultView
          v-else-if="dashboardResults[view.id]"
          :result="dashboardResults[view.id]"
          :kind="view.kind"
          :field-ids="view.config?.fieldIds"
          :scale-bars="view.config?.scaleBars"
          :show-updated-at="view.config?.showUpdatedAt"
          compact
          :view-config="view.config"
          :page-url="base + '/views/' + view.id + '/run'"
        />
        <div v-else class="dashboard-loading" role="status">
          Beregner {{ view.name }}…
        </div>
      </article>
    </div>
    <Teleport to="body">
      <div
        v-if="dragging"
        v-position="dragPosition"
        class="card-drag-preview"
        aria-hidden="true"
      >
        <DragHandleIcon :size="18" /><span>{{ draggedName }}</span>
      </div>
    </Teleport>
  </template>
  <div v-else-if="!views.length" class="empty-state">
    <ChartIcon :size="30" />
    <h3>Genbrug en formular til indtastning</h3>
    <p>Vælg de felter, der skal indgå, og gem formularen.</p>
    <button v-if="canWrite" class="button secondary" @click="create('form')">
      Opret formular
    </button>
  </div>
</template>
<style scoped lang="scss" src="../styles/components/ViewsPanel.scss"></style>
